import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { PATHS } from "../routes/paths";
import {
  atualizarCidade,
  getCidadeAdmin,
  listCidadesAdmin,
} from "../services/cidades";
import CidadeForm from "../components/Admin/CidadeForm/CidadeForm";
import FormActions from "../components/Admin/FormActions/FormActions";
import TituloNucleo from "../components/RegionalHub/TituloNucleo/TituloNucleo";
import Slideshow from "../components/RegionalHub/Slideshow/Slideshow";
import Apresentacao from "../components/RegionalHub/Apresentacao/Apresentacao";
import NucleoIndicadores from "../components/RegionalHub/Indicadores/Indicadores";
import Parcerias from "../components/RegionalHub/Parcerias/Parcerias";
import Equipe from "../components/QuemSomos/Equipe/Equipe";
import ComoAjudar from "../components/RegionalHub/ComoAjudar/ComoAjudar";
import { SuccessDialog } from "../components/Admin/Dialogs";
import {
  getStaticRegionalHub,
  LAVRAS_CONTACT,
  LAVRAS_HISTORY_TEXT,
  LAVRAS_INDICATORS,
  LAVRAS_MAINTAINED_TEXT,
  LAVRAS_TEAM,
} from "./RegionalHub";
import "./AdminCidade.css";
import { normalizeCityIndicators } from "../utils/cityIndicators";

const DEFAULT_TITLE = "Núcleo 7 Geração - ProSer";

function getFallbackCity(slug) {
  const staticHub = getStaticRegionalHub(slug);
  if (!staticHub) return null;

  return {
    _id: `fallback-${slug}`,
    _isFallback: true,
    name: staticHub.city,
    city: staticHub.city,
    slug,
    description: LAVRAS_MAINTAINED_TEXT,
    active: true,
    presentation: {
      title: staticHub.title || DEFAULT_TITLE,
      subtitle: "",
      description: LAVRAS_HISTORY_TEXT,
    },
    slideshow: staticHub.slideshow || [],
    indicators: LAVRAS_INDICATORS,
    team: LAVRAS_TEAM,
    teamConfigured: false,
    contact_info: LAVRAS_CONTACT,
  };
}

function mergeCityData(slug, apiCity) {
  const fallback = getFallbackCity(slug) || {};

  if (!apiCity) return fallback;

  return {
    ...fallback,
    ...apiCity,
    presentation: {
      ...fallback.presentation,
      ...apiCity.presentation,
    },
    contact_info: {
      ...fallback.contact_info,
      ...apiCity.contact_info,
    },
    slideshow: apiCity.slideshow?.length
      ? apiCity.slideshow
      : fallback.slideshow || [],
    indicators: normalizeCityIndicators(
      apiCity.indicators,
      fallback.indicators,
    ),
    team: apiCity.teamConfigured === true
      ? apiCity.team || []
      : apiCity.team?.length
        ? apiCity.team
        : fallback.team || [],
  };
}

function hasPersistedId(city) {
  const id = String(city?._id || city?.id || "");
  return Boolean(id) && !id.startsWith("fallback-");
}

export default function AdminCidade() {
  const { slug } = useParams();
  const location = useLocation();
  const [city, setCity] = useState(() => getFallbackCity(slug));
  const [loading, setLoading] = useState(!getFallbackCity(slug));
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(() =>
    Boolean(location.state?.startEditing),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    let active = true;

    async function loadCity() {
      setLoading(!getFallbackCity(slug));
      setLoadError("");

      try {
        const stateCity =
          location.state?.cidade?.slug === slug
            ? location.state.cidade
            : null;
        const cities = stateCity ? [stateCity] : await listCidadesAdmin();
        const selected =
          stateCity || cities.find((item) => item.slug === slug);

        if (!selected) {
          throw new Error("Cidade não encontrada");
        }

        const complete = hasPersistedId(selected)
          ? await getCidadeAdmin(selected._id || selected.id)
          : selected;

        if (active) setCity(mergeCityData(slug, complete));
      } catch {
        if (!active) return;

        const fallback = getFallbackCity(slug);
        if (fallback) {
          setCity(fallback);
        } else {
          setLoadError("Não foi possível carregar esta cidade.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    loadCity();
    return () => {
      active = false;
    };
  }, [location.state, slug]);

  const staticHub = getStaticRegionalHub(slug);
  const historySections = useMemo(
    () => [
      {
        title: "História e Atuação",
        paragraphs: [
          city?.presentation?.description || LAVRAS_HISTORY_TEXT,
        ],
      },
      {
        title: "Quem mantém este núcleo?",
        paragraphs: [city?.description || LAVRAS_MAINTAINED_TEXT],
      },
    ],
    [city],
  );

  const saveCity = async (form) => {
    if (!hasPersistedId(city)) {
      setSaveError(
        "A cidade está em modo de visualização local. Inicie o back e conecte o banco para salvar alterações.",
      );
      return;
    }

    setSaving(true);
    setSaveError("");

    try {
      const updated = await atualizarCidade(city._id || city.id, form);
      setCity(mergeCityData(slug, updated));
      setEditing(false);
      setFeedback("Alterações salvas com sucesso!");
    } catch (error) {
      setSaveError(
        error?.response?.data?.message ||
          "Não foi possível salvar. Verifique os campos e tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading && !city) {
    return <p className="admin-cidade__state">Carregando cidade...</p>;
  }

  if (loadError || !city) {
    return (
      <div className="admin-cidade__state">
        <p>{loadError || "Cidade não encontrada."}</p>
        <Link to={PATHS.adminCidades}>Voltar para cidades</Link>
      </div>
    );
  }

  return (
    <div className={`admin-cidade${editing ? " admin-cidade--editing" : ""}`}>
      <header className="admin-cidade__header">
        <Link to={PATHS.adminCidades}>← Todas as cidades</Link>
        <h1>EDITAR SEÇÃO</h1>
        <p>Onde Estamos - {city.city || city.name}</p>
      </header>

      <SuccessDialog
        open={Boolean(feedback)}
        message={feedback}
        onClose={() => setFeedback("")}
      />

      {editing ? (
        <section className="admin-cidade__editor" aria-label="Editar cidade">
          <CidadeForm
            initialValue={city}
            onSubmit={saveCity}
            onCancel={() => {
              setEditing(false);
              setSaveError("");
            }}
            submitting={saving}
          />
          {saveError && (
            <p className="admin-cidade__error" role="alert">
              {saveError}
            </p>
          )}
        </section>
      ) : (
        <>
          <TituloNucleo
            nome={
              city?.presentation?.title || staticHub?.title || DEFAULT_TITLE
            }
            cidade={city.city || staticHub?.city}
            estado={city._isFallback ? staticHub?.state || "" : city.state ?? ""}
          />

          <Slideshow
            slides={city.slideshow}
            fallbackSlides={staticHub?.slideshow || []}
          />

          <Apresentacao sections={historySections} />

          <NucleoIndicadores
            indicators={city.indicators}
            fallbackIndicators={LAVRAS_INDICATORS}
            imageUrl={city.indicatorsImage || staticHub?.indicatorsImageUrl}
            showOverlay={false}
          />

          <Parcerias partners={city.partnerships || []} />
          <Equipe
            team={{
              title: "Equipe",
              members: (city.team || []).map((member) => ({
                ...member,
                photo: member.photoUrl || member.photo,
              })),
            }}
          />
          <ComoAjudar
            text={city.helpText || ""}
            contact={{ ...LAVRAS_CONTACT, ...city.contact_info }}
          />

          <div className="admin-cidade__actions">
            <FormActions editing={false} onEdit={() => setEditing(true)} />
          </div>
        </>
      )}
    </div>
  );
}
